import { Tool } from '@modelcontextprotocol/sdk/types.js';
import {
  SuggestTestScenariosInputSchema,
  type Config,
  type TestScenarioResult,
  type DetectedMethod,
  type TestScenario,
} from '../types/index.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { logger } from '../lib/logger.js';
import { findFiles, readText } from '../utils/fs.js';
import path from 'path';

export const suggestTestScenariosTool: Tool = {
  name: 'suggest_test_scenarios',
  description: 'Analyze source code and suggest test scenarios based on detected methods, parameters, and patterns. Generates comprehensive test case recommendations for SmartStack components.',
  inputSchema: {
    type: 'object',
    properties: {
      target: {
        type: 'string',
        enum: ['entity', 'service', 'controller', 'file'],
        description: 'Type of target to analyze',
      },
      name: {
        type: 'string',
        description: 'Component name or file path',
      },
      depth: {
        type: 'string',
        enum: ['basic', 'comprehensive', 'security-focused'],
        default: 'comprehensive',
        description: 'Depth of analysis',
      },
    },
    required: ['target', 'name'],
  },
};

// C# parsing patterns
const PATTERNS = {
  // Method signatures
  publicMethod: /public\s+(?:async\s+)?(?:Task<)?(\w+)>?\s+(\w+)\s*\(([^)]*)\)/g,
  // Parameter extraction
  parameter: /(?:(\w+)\s+)?(\w+)\s+(\w+)(?:\s*=\s*([^,)]+))?/g,
  // Property
  property: /public\s+(?:virtual\s+)?(\w+)\??\s+(\w+)\s*\{\s*get;/g,
  // Validation attributes
  validationAttribute: /\[(Required|MaxLength|MinLength|Range|EmailAddress|Phone|Url|RegularExpression)\s*(?:\(([^)]*)\))?\]/g,
  // Entity patterns
  baseEntity: /:\s*(?:BaseEntity|SystemEntity)/,
  tenantEntity: /ITenantEntity/,
  // Factory method
  factoryMethod: /public\s+static\s+\w+\s+Create\s*\(/,
  // Soft delete
  softDelete: /public\s+void\s+SoftDelete/,
};

export async function handleSuggestTestScenarios(
  args: unknown,
  config: Config
): Promise<string> {
  const input = SuggestTestScenariosInputSchema.parse(args);

  logger.info('Suggesting test scenarios', { target: input.target, name: input.name, depth: input.depth });

  const structure = await findSmartStackStructure(config.smartstack.projectPath);

  const result: TestScenarioResult = {
    target: input.name,
    targetType: input.target as 'entity' | 'service' | 'controller' | 'file',
    detectedMethods: [],
    scenarios: [],
    coverage: {
      happyPath: 0,
      validation: 0,
      errorHandling: 0,
      security: 0,
      edgeCases: 0,
    },
  };

  try {
    // Find and read the source file
    const sourceFile = await findSourceFile(input.target, input.name, structure, config);
    if (!sourceFile) {
      return `Error: Could not find ${input.target} '${input.name}'`;
    }

    const content = await readText(sourceFile);

    // Parse the source code
    result.detectedMethods = parseSourceCode(content);

    // Generate test scenarios
    result.scenarios = generateScenarios(
      result.detectedMethods,
      content,
      input.target,
      input.depth
    );

    // Calculate coverage distribution
    calculateCoverageDistribution(result);

  } catch (error) {
    logger.error('Error suggesting test scenarios', error);
    return `Error: ${error instanceof Error ? error.message : String(error)}`;
  }

  return formatScenariosResult(result, input.depth);
}

// ============================================================================
// Source File Finding
// ============================================================================

async function findSourceFile(
  target: string,
  name: string,
  structure: { root: string; domain?: string; application?: string; infrastructure?: string; api?: string },
  _config: Config
): Promise<string | null> {
  let searchPath: string;
  let pattern: string;

  switch (target) {
    case 'entity':
      searchPath = structure.domain || structure.root;
      pattern = `**/${name}.cs`;
      break;
    case 'service':
      searchPath = structure.application || structure.root;
      pattern = `**/${name}Service.cs`;
      break;
    case 'controller':
      searchPath = structure.api || structure.root;
      pattern = `**/${name}Controller.cs`;
      break;
    case 'file':
      return path.isAbsolute(name) ? name : path.join(structure.root, name);
    default:
      return null;
  }

  const files = await findFiles(pattern, { cwd: searchPath });
  if (files.length === 0) {
    // Try alternative patterns
    const altPattern = `**/*${name}*.cs`;
    const altFiles = await findFiles(altPattern, { cwd: searchPath });
    if (altFiles.length > 0) {
      return path.join(searchPath, altFiles[0]);
    }
    return null;
  }

  return path.join(searchPath, files[0]);
}

// ============================================================================
// Source Code Parsing
// ============================================================================

function parseSourceCode(content: string): DetectedMethod[] {
  const methods: DetectedMethod[] = [];

  // Reset regex lastIndex
  PATTERNS.publicMethod.lastIndex = 0;

  let match;
  while ((match = PATTERNS.publicMethod.exec(content)) !== null) {
    const returnType = match[1];
    const methodName = match[2];
    const parametersStr = match[3];

    // Parse parameters
    const parameters: string[] = [];
    if (parametersStr.trim()) {
      const params = parametersStr.split(',');
      for (const param of params) {
        const trimmed = param.trim();
        if (trimmed) {
          // Extract parameter name
          const parts = trimmed.split(/\s+/);
          if (parts.length >= 2) {
            parameters.push(`${parts[parts.length - 2]} ${parts[parts.length - 1]}`);
          }
        }
      }
    }

    // Determine visibility and async
    const isAsync = content.substring(Math.max(0, match.index - 20), match.index).includes('async');
    const isPublic = content.substring(Math.max(0, match.index - 20), match.index).includes('public');

    if (isPublic && !methodName.startsWith('get_') && !methodName.startsWith('set_')) {
      methods.push({
        name: methodName,
        returnType,
        parameters,
        isAsync,
        visibility: 'public',
      });
    }
  }

  return methods;
}

// ============================================================================
// Scenario Generation
// ============================================================================

function generateScenarios(
  methods: DetectedMethod[],
  content: string,
  target: string,
  depth: string
): TestScenario[] {
  const scenarios: TestScenario[] = [];

  // Detect patterns in source
  const isEntity = PATTERNS.baseEntity.test(content);
  const hasTenant = PATTERNS.tenantEntity.test(content);
  const hasFactoryMethod = PATTERNS.factoryMethod.test(content);
  const hasSoftDelete = PATTERNS.softDelete.test(content);

  for (const method of methods) {
    // Generate scenarios based on method name patterns
    const methodScenarios = generateMethodScenarios(
      method,
      { isEntity, hasTenant, hasFactoryMethod, hasSoftDelete },
      target,
      depth
    );
    scenarios.push(...methodScenarios);
  }

  // Add entity-specific scenarios
  if (isEntity && target === 'entity') {
    scenarios.push(...generateEntityScenarios(content, hasTenant, hasSoftDelete, depth));
  }

  // Add security scenarios for comprehensive/security-focused
  if (depth !== 'basic') {
    scenarios.push(...generateSecurityScenarios(methods, hasTenant, target));
  }

  return scenarios;
}

function generateMethodScenarios(
  method: DetectedMethod,
  patterns: { isEntity: boolean; hasTenant: boolean; hasFactoryMethod: boolean; hasSoftDelete: boolean },
  _target: string,
  depth: string
): TestScenario[] {
  const scenarios: TestScenario[] = [];
  const methodLower = method.name.toLowerCase();

  // Happy path scenarios
  scenarios.push({
    methodName: method.name,
    scenarioName: 'Happy Path',
    description: `Test ${method.name} with valid data`,
    type: 'happy-path',
    priority: 'critical',
    testMethodName: `${method.name}_WhenValidData_ShouldSucceed`,
    assertions: generateAssertions(method, 'happy-path'),
  });

  // CRUD-specific scenarios
  if (methodLower.includes('getbyid') || methodLower.includes('get')) {
    scenarios.push({
      methodName: method.name,
      scenarioName: 'Not Found',
      description: `Test ${method.name} when entity does not exist`,
      type: 'error-handling',
      priority: 'high',
      testMethodName: `${method.name}_WhenNotExists_ShouldReturnNull`,
      assertions: ['result.Should().BeNull()'],
    });
  }

  if (methodLower.includes('create') || methodLower.includes('add')) {
    scenarios.push({
      methodName: method.name,
      scenarioName: 'Validation Failure',
      description: `Test ${method.name} with invalid data`,
      type: 'validation',
      priority: 'high',
      testMethodName: `${method.name}_WhenInvalidData_ShouldThrow`,
      assertions: ['act.Should().ThrowAsync<ValidationException>()'],
    });

    if (patterns.hasTenant) {
      scenarios.push({
        methodName: method.name,
        scenarioName: 'Missing Tenant',
        description: `Test ${method.name} without tenant context`,
        type: 'security',
        priority: 'critical',
        testMethodName: `${method.name}_WhenNoTenant_ShouldThrow`,
        assertions: ['act.Should().ThrowAsync<ArgumentException>()'],
      });
    }
  }

  if (methodLower.includes('update')) {
    scenarios.push({
      methodName: method.name,
      scenarioName: 'Not Found',
      description: `Test ${method.name} when entity does not exist`,
      type: 'error-handling',
      priority: 'high',
      testMethodName: `${method.name}_WhenNotExists_ShouldThrow`,
      assertions: ['act.Should().ThrowAsync<InvalidOperationException>()'],
    });

    if (depth !== 'basic') {
      scenarios.push({
        methodName: method.name,
        scenarioName: 'Concurrent Update',
        description: `Test ${method.name} with concurrent modification`,
        type: 'edge-case',
        priority: 'medium',
        testMethodName: `${method.name}_WhenConcurrentUpdate_ShouldHandleConflict`,
        assertions: ['Should handle DbUpdateConcurrencyException'],
      });
    }
  }

  if (methodLower.includes('delete')) {
    scenarios.push({
      methodName: method.name,
      scenarioName: 'Not Found',
      description: `Test ${method.name} when entity does not exist`,
      type: 'error-handling',
      priority: 'high',
      testMethodName: `${method.name}_WhenNotExists_ShouldReturnFalse`,
      assertions: ['result.Should().BeFalse()'],
    });

    if (patterns.hasSoftDelete) {
      scenarios.push({
        methodName: method.name,
        scenarioName: 'Soft Delete',
        description: `Verify ${method.name} performs soft delete`,
        type: 'happy-path',
        priority: 'high',
        testMethodName: `${method.name}_WhenCalled_ShouldSoftDelete`,
        assertions: ['entity.IsDeleted.Should().BeTrue()', 'entity.DeletedAt.Should().NotBeNull()'],
      });
    }
  }

  // Async cancellation scenarios
  if (method.isAsync && depth !== 'basic') {
    scenarios.push({
      methodName: method.name,
      scenarioName: 'Cancellation',
      description: `Test ${method.name} with cancellation token`,
      type: 'edge-case',
      priority: 'low',
      testMethodName: `${method.name}_WhenCancelled_ShouldThrowOperationCancelled`,
      assertions: ['act.Should().ThrowAsync<OperationCanceledException>()'],
    });
  }

  return scenarios;
}

function generateEntityScenarios(
  content: string,
  hasTenant: boolean,
  hasSoftDelete: boolean,
  depth: string
): TestScenario[] {
  const scenarios: TestScenario[] = [];

  // Factory method scenarios
  if (PATTERNS.factoryMethod.test(content)) {
    scenarios.push({
      methodName: 'Create',
      scenarioName: 'Factory Method',
      description: 'Test entity creation via factory method',
      type: 'happy-path',
      priority: 'critical',
      testMethodName: 'Create_WhenValidData_ShouldCreateEntity',
      assertions: [
        'entity.Should().NotBeNull()',
        'entity.Id.Should().NotBeEmpty()',
        'entity.CreatedAt.Should().BeCloseTo(DateTime.UtcNow)',
      ],
    });

    if (hasTenant) {
      scenarios.push({
        methodName: 'Create',
        scenarioName: 'Invalid Tenant',
        description: 'Test entity creation with empty tenant ID',
        type: 'validation',
        priority: 'critical',
        testMethodName: 'Create_WhenEmptyTenantId_ShouldThrow',
        assertions: ['act.Should().Throw<ArgumentException>()'],
      });
    }
  }

  // Soft delete scenarios
  if (hasSoftDelete) {
    scenarios.push({
      methodName: 'SoftDelete',
      scenarioName: 'Soft Delete',
      description: 'Test soft delete sets IsDeleted and DeletedAt',
      type: 'happy-path',
      priority: 'high',
      testMethodName: 'SoftDelete_WhenCalled_ShouldSetDeletedFields',
      assertions: [
        'entity.IsDeleted.Should().BeTrue()',
        'entity.DeletedAt.Should().NotBeNull()',
        'entity.DeletedBy.Should().Be(deletedBy)',
      ],
    });

    if (depth !== 'basic') {
      scenarios.push({
        methodName: 'Restore',
        scenarioName: 'Restore After Delete',
        description: 'Test restoring a soft-deleted entity',
        type: 'happy-path',
        priority: 'medium',
        testMethodName: 'Restore_WhenSoftDeleted_ShouldClearDeletedFields',
        assertions: [
          'entity.IsDeleted.Should().BeFalse()',
          'entity.DeletedAt.Should().BeNull()',
        ],
      });
    }
  }

  // Audit trail scenarios
  if (depth !== 'basic') {
    scenarios.push({
      methodName: 'Update',
      scenarioName: 'Audit Trail',
      description: 'Test that Update sets audit fields',
      type: 'happy-path',
      priority: 'medium',
      testMethodName: 'Update_WhenCalled_ShouldSetAuditFields',
      assertions: [
        'entity.UpdatedAt.Should().NotBeNull()',
        'entity.UpdatedBy.Should().Be(updatedBy)',
        'entity.CreatedAt.Should().Be(originalCreatedAt)',
      ],
    });
  }

  return scenarios;
}

function generateSecurityScenarios(
  _methods: DetectedMethod[],
  hasTenant: boolean,
  _target: string
): TestScenario[] {
  const scenarios: TestScenario[] = [];

  if (hasTenant) {
    scenarios.push({
      methodName: 'TenantIsolation',
      scenarioName: 'Cross-Tenant Access',
      description: 'Test that entities from other tenants are not accessible',
      type: 'security',
      priority: 'critical',
      testMethodName: 'GetById_WhenDifferentTenant_ShouldReturnNull',
      assertions: ['result.Should().BeNull()'],
    });
  }

  if (_target === 'controller') {
    scenarios.push({
      methodName: 'Authorization',
      scenarioName: 'Unauthorized Access',
      description: 'Test that unauthorized requests are rejected',
      type: 'security',
      priority: 'critical',
      testMethodName: 'GetAll_WhenUnauthorized_ShouldReturn401',
      assertions: ['response.StatusCode.Should().Be(HttpStatusCode.Unauthorized)'],
    });

    scenarios.push({
      methodName: 'InputValidation',
      scenarioName: 'SQL Injection',
      description: 'Test that SQL injection attempts are handled',
      type: 'security',
      priority: 'high',
      testMethodName: 'Create_WhenSqlInjectionAttempt_ShouldSanitize',
      assertions: ['Should not execute malicious SQL'],
    });

    scenarios.push({
      methodName: 'InputValidation',
      scenarioName: 'XSS Prevention',
      description: 'Test that XSS attempts are handled',
      type: 'security',
      priority: 'high',
      testMethodName: 'Create_WhenXssAttempt_ShouldSanitize',
      assertions: ['Should not store or return script tags'],
    });
  }

  return scenarios;
}

function generateAssertions(method: DetectedMethod, type: string): string[] {
  const assertions: string[] = [];

  if (type === 'happy-path') {
    if (method.returnType === 'void' || method.returnType === 'Task') {
      assertions.push('Should complete without exception');
    } else if (method.returnType.includes('bool') || method.returnType === 'Boolean') {
      assertions.push('result.Should().BeTrue()');
    } else {
      assertions.push('result.Should().NotBeNull()');
    }
  }

  return assertions;
}

function calculateCoverageDistribution(result: TestScenarioResult): void {
  const total = result.scenarios.length;
  if (total === 0) return;

  const counts = {
    happyPath: result.scenarios.filter(s => s.type === 'happy-path').length,
    validation: result.scenarios.filter(s => s.type === 'validation').length,
    errorHandling: result.scenarios.filter(s => s.type === 'error-handling').length,
    security: result.scenarios.filter(s => s.type === 'security').length,
    edgeCases: result.scenarios.filter(s => s.type === 'edge-case' || s.type === 'performance').length,
  };

  result.coverage = {
    happyPath: Math.round((counts.happyPath / total) * 100),
    validation: Math.round((counts.validation / total) * 100),
    errorHandling: Math.round((counts.errorHandling / total) * 100),
    security: Math.round((counts.security / total) * 100),
    edgeCases: Math.round((counts.edgeCases / total) * 100),
  };
}

// ============================================================================
// Formatting
// ============================================================================

function formatScenariosResult(result: TestScenarioResult, depth: string): string {
  const lines: string[] = [];

  lines.push(`# Test Scenarios for ${result.target}`);
  lines.push('');
  lines.push(`> Analysis depth: **${depth}**`);
  lines.push('');

  // Detected methods
  if (result.detectedMethods.length > 0) {
    lines.push('## Detected Methods');
    lines.push('');
    lines.push('| Method | Return Type | Parameters | Async |');
    lines.push('|--------|-------------|------------|-------|');
    for (const method of result.detectedMethods) {
      const params = method.parameters.length > 0 ? method.parameters.join(', ') : 'none';
      lines.push(`| ${method.name} | ${method.returnType} | ${params} | ${method.isAsync ? 'Yes' : 'No'} |`);
    }
    lines.push('');
  }

  // Coverage distribution
  lines.push('## Scenario Coverage Distribution');
  lines.push('');
  lines.push('```');
  lines.push(`Happy Path:     ${'█'.repeat(Math.floor(result.coverage.happyPath / 5))}${'░'.repeat(20 - Math.floor(result.coverage.happyPath / 5))} ${result.coverage.happyPath}%`);
  lines.push(`Validation:     ${'█'.repeat(Math.floor(result.coverage.validation / 5))}${'░'.repeat(20 - Math.floor(result.coverage.validation / 5))} ${result.coverage.validation}%`);
  lines.push(`Error Handling: ${'█'.repeat(Math.floor(result.coverage.errorHandling / 5))}${'░'.repeat(20 - Math.floor(result.coverage.errorHandling / 5))} ${result.coverage.errorHandling}%`);
  lines.push(`Security:       ${'█'.repeat(Math.floor(result.coverage.security / 5))}${'░'.repeat(20 - Math.floor(result.coverage.security / 5))} ${result.coverage.security}%`);
  lines.push(`Edge Cases:     ${'█'.repeat(Math.floor(result.coverage.edgeCases / 5))}${'░'.repeat(20 - Math.floor(result.coverage.edgeCases / 5))} ${result.coverage.edgeCases}%`);
  lines.push('```');
  lines.push('');

  // Scenarios by priority
  lines.push('## Suggested Test Scenarios');
  lines.push('');

  const priorities: Array<'critical' | 'high' | 'medium' | 'low'> = ['critical', 'high', 'medium', 'low'];
  for (const priority of priorities) {
    const scenarios = result.scenarios.filter(s => s.priority === priority);
    if (scenarios.length === 0) continue;

    const emoji = priority === 'critical' ? '🔴' : priority === 'high' ? '🟠' : priority === 'medium' ? '🟡' : '🟢';
    lines.push(`### ${emoji} ${priority.charAt(0).toUpperCase() + priority.slice(1)} Priority`);
    lines.push('');

    for (const scenario of scenarios) {
      const typeEmoji = getTypeEmoji(scenario.type);
      lines.push(`#### ${typeEmoji} ${scenario.methodName} - ${scenario.scenarioName}`);
      lines.push('');
      lines.push(`**Description:** ${scenario.description}`);
      lines.push('');
      lines.push(`**Test Method:** \`${scenario.testMethodName}\``);
      lines.push('');
      if (scenario.assertions.length > 0) {
        lines.push('**Assertions:**');
        for (const assertion of scenario.assertions) {
          lines.push(`- \`${assertion}\``);
        }
        lines.push('');
      }
    }
  }

  // Quick generation command
  lines.push('## Quick Test Generation');
  lines.push('');
  lines.push('Generate tests for this component:');
  lines.push('');
  lines.push('```');
  lines.push(`scaffold_tests(target: "${result.targetType}", name: "${result.target}", testTypes: ["unit", "security"])`);
  lines.push('```');
  lines.push('');

  return lines.join('\n');
}

function getTypeEmoji(type: string): string {
  switch (type) {
    case 'happy-path': return '✅';
    case 'validation': return '🔍';
    case 'error-handling': return '⚠️';
    case 'security': return '🔒';
    case 'edge-case': return '🧪';
    case 'performance': return '⚡';
    default: return '📋';
  }
}
